import type { Location } from 'history';
import { matchRoutes, type RouteConfig } from 'react-router-config';
import { createRadioBrowseContext } from './getBrowseContext';
import { createRadioProfileContext } from './getProfileContext';

export type RouteProps = Record<string, unknown> & {
  guideContext: Record<string, unknown> & { guideId?: string; itemToken?: string };
  matchUrl?: string;
};

interface DeriveRoutePropsArgs {
  routes: RouteConfig[];
  location: Location<{ itemToken?: string } | undefined>;
  isDiscord?: boolean;
  hasRouteError?: boolean;
}

const DISCORD_OVERRIDES = {
  isDiscord: true,
  hideLeft: true,
  hideRight: true,
  showNowPlayingAndVideoAdDialogsOnly: true,
  noHeader: true, // Note: this is for the web header, so showBrowsiesHeader will apply independently
  showBrowsiesHeader: true,
  hideAds: true,
};

/**
 * Props describing the current page: the matched route's `routeLevelProps`, app-wide overrides
 * (Discord, route errors), and the guide item the URL points at.
 */
export function deriveRouteProps({
  routes,
  location,
  isDiscord,
  hasRouteError,
}: DeriveRoutePropsArgs): RouteProps {
  const currentRoute = matchRoutes(routes, location.pathname)[0];
  const routeContextArgs = { location, ...currentRoute };
  const guideContext =
    createRadioProfileContext(routeContextArgs) || createRadioBrowseContext(routeContextArgs) || {};

  return {
    ...currentRoute?.route?.routeLevelProps,
    ...(isDiscord && DISCORD_OVERRIDES),
    ...(hasRouteError && { hideAds: true }),
    // `match` only updates reliably at the route level, so nested components detect page changes
    // by comparing this instead.
    matchUrl: currentRoute?.match?.url,
    guideContext: { ...guideContext, itemToken: location.state?.itemToken },
  };
}
